import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, XCircle, Clock, MapPin, Calendar } from 'lucide-react';
import { bookingPublicApi, ManageAppointment } from '../../services/api';
import { brandingVars, brandingLogo, BookingBranding } from './bookingBranding';
import './Booking.css';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDateTime = (startLocal: string, tz: string): string => {
  const datePart = startLocal.split('T')[0];
  const timePart = (startLocal.split('T')[1] ?? '').split(/[+\-Z]/)[0];
  const [hourStr, minuteStr] = timePart.split(':');
  const hour = parseInt(hourStr ?? '0', 10);
  const minute = minuteStr ?? '00';
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;

  const [year, month, day] = datePart.split('-').map(Number);
  const d = new Date(year, month - 1, day);
  const dateLabel = d.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
  return `${dateLabel} at ${displayHour}:${minute} ${ampm}${tz ? ` (${tz})` : ''}`;
};

// ─── ManageBooking ────────────────────────────────────────────────────────────

type PageState = 'loading' | 'error' | 'loaded' | 'cancelling' | 'cancelled';

const ManageBooking: React.FC = () => {
  const { token } = useParams<{ token: string }>();

  const [pageState, setPageState] = useState<PageState>('loading');
  const [errorMsg, setErrorMsg] = useState('');
  const [appointment, setAppointment] = useState<ManageAppointment | null>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const [branding, setBranding] = useState<BookingBranding | null>(null);
  const bkStyle = useMemo(() => brandingVars(branding), [branding]);
  const logoUrl = brandingLogo(branding);

  const load = useCallback(async () => {
    if (!token) {
      setErrorMsg('Invalid manage link.');
      setPageState('error');
      return;
    }
    try {
      const appt = await bookingPublicApi.getManage(token);
      setAppointment(appt);
      setBranding(appt.branding ?? null);
      setPageState('loaded');
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 404) {
        setErrorMsg('This booking was not found. The link may have expired or be incorrect.');
      } else {
        setErrorMsg('Failed to load booking details. Please try again.');
      }
      setPageState('error');
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCancel = async () => {
    if (!token) return;
    setPageState('cancelling');
    setCancelError('');
    try {
      await bookingPublicApi.cancel(token);
      if (appointment) {
        setAppointment({ ...appointment, status: 'cancelled' });
      }
      setPageState('cancelled');
    } catch (err: any) {
      setCancelError(
        err?.response?.data?.detail ?? 'Cancellation failed. Please try again.'
      );
      setPageState('loaded');
    }
  };

  // ─ Loading ─
  // (branding not yet known — correctly renders unbranded)
  if (pageState === 'loading') {
    return (
      <div className="bk-page bk-scope">
        <div className="bk-card">
          <p className="bk-desc" style={{ textAlign: 'center' }}>Loading...</p>
        </div>
      </div>
    );
  }

  // ─ Error ─
  // (branding not yet known — correctly renders unbranded)
  if (pageState === 'error') {
    return (
      <div className="bk-page bk-scope">
        <div className="bk-card">
          <h1 className="bk-title">Not Found</h1>
          <p className="bk-desc">{errorMsg}</p>
        </div>
      </div>
    );
  }

  // ─ Cancelled ─
  if (pageState === 'cancelled' || (appointment && appointment.status === 'cancelled')) {
    return (
      <div className="bk-page bk-scope" style={bkStyle}>
        <div className="bk-card">
          {logoUrl && (
            <img
              className="bk-logo"
              src={logoUrl}
              alt=""
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          )}
          <div className="bk-success-icon" style={{ color: 'rgba(15, 23, 42, 0.62)' }}>
            <XCircle size={52} />
          </div>
          <h2 className="bk-success-title">Appointment Cancelled</h2>
          <p className="bk-success-msg">
            Your appointment has been cancelled. You can close this page.
          </p>
        </div>
      </div>
    );
  }

  if (!appointment) return null;

  const isBooked = appointment.status === 'booked';

  return (
    <div className="bk-page bk-scope" style={bkStyle}>
      <div className="bk-card">
        {logoUrl && (
            <img
              className="bk-logo"
              src={logoUrl}
              alt=""
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          )}
        <div className="bk-success-icon">
          <CheckCircle size={52} />
        </div>
        <h2 className="bk-success-title">Your Appointment</h2>

        {appointment.name && (
          <p className="bk-subtitle" style={{ textAlign: 'center', marginBottom: 'var(--space-4)' }}>
            {appointment.name}
          </p>
        )}

        {appointment.resource_name && (
          <p className="bk-desc" style={{ textAlign: 'center', marginTop: 'calc(-1 * var(--space-2))', marginBottom: 'var(--space-4)' }}>
            with {appointment.resource_name}
          </p>
        )}

        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 'var(--space-6)' }}>
          <span className={`bk-status-badge ${appointment.status}`}>
            {appointment.status}
          </span>
        </div>

        <div className="bk-confirm-detail">
          <div className="bk-confirm-detail-row">
            <Clock size={16} style={{ flexShrink: 0, color: 'rgba(15, 23, 42, 0.62)', marginTop: 2 }} />
            <span>{formatDateTime(appointment.start_local, appointment.timezone)}</span>
          </div>
          {appointment.location && (
            <div className="bk-confirm-detail-row">
              <MapPin size={16} style={{ flexShrink: 0, color: 'rgba(15, 23, 42, 0.62)', marginTop: 2 }} />
              <span>{appointment.location}</span>
            </div>
          )}
          {appointment.timezone && (
            <div className="bk-confirm-detail-row">
              <Calendar size={16} style={{ flexShrink: 0, color: 'rgba(15, 23, 42, 0.62)', marginTop: 2 }} />
              <span>{appointment.timezone}</span>
            </div>
          )}
        </div>

        {isBooked && !confirmCancel && (
          <button
            type="button"
            className="bk-btn-secondary"
            style={{ width: '100%', marginTop: 'var(--space-4)' }}
            onClick={() => setConfirmCancel(true)}
          >
            Cancel appointment
          </button>
        )}

        {isBooked && confirmCancel && (
          <div style={{ marginTop: 'var(--space-4)' }}>
            <p className="bk-desc" style={{ textAlign: 'center', marginBottom: 'var(--space-4)' }}>
              Are you sure you want to cancel this appointment?
            </p>
            {cancelError && <p className="bk-error" style={{ textAlign: 'center' }}>{cancelError}</p>}
            <div className="bk-actions">
              <button
                type="button"
                className="bk-btn-secondary"
                onClick={() => { setConfirmCancel(false); setCancelError(''); }}
                disabled={pageState === 'cancelling'}
              >
                Keep it
              </button>
              <button
                type="button"
                className="bk-btn-primary"
                style={{ background: 'var(--danger, #e53e3e)' }}
                onClick={handleCancel}
                disabled={pageState === 'cancelling'}
              >
                {pageState === 'cancelling' ? 'Cancelling...' : 'Yes, cancel'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageBooking;
